import { useEffect, useState, useCallback } from "react";
import api, { apiErr, photoUrl } from "@/lib/api";
import { compressImage } from "@/lib/image";
import { CIRCULAR } from "@/constants/testIds";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { fmtSGD } from "@/lib/format";
import {
  Recycle, Plus, Sparkles, Loader2, Bot, Trash2, Pencil, Camera, Target,
  Leaf, Boxes, Package, CheckCircle2, AlertTriangle, Handshake,
} from "lucide-react";
import { toast } from "sonner";

const PATHWAY_CLS = {
  Avoid: "bg-emerald-100 text-emerald-800", Reduce: "bg-emerald-50 text-emerald-700",
  Reuse: "bg-teal-100 text-teal-800", Upcycle: "bg-lime-100 text-lime-800",
  Recycle: "bg-sky-100 text-sky-800", Recover: "bg-amber-100 text-amber-800",
  Dispose: "bg-rose-100 text-rose-700", Repurpose: "bg-lime-100 text-lime-800",
  "Take-back": "bg-indigo-100 text-indigo-800",
};

function PhotoPicker({ ids, setIds, testid }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    for (const f of files) {
      try {
        const dataUrl = await compressImage(f);
        const r = await api.post("/photos", { data_url: dataUrl, caption: "Circular waste evidence" });
        setIds((prev) => [...prev, r.data.id]);
      } catch (err) { toast.error(apiErr(err)); }
    }
    setBusy(false);
  };
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {ids.map((id) => (
          <div key={id} className="relative">
            <img src={photoUrl({ id })} alt="" className="h-12 w-12 rounded border border-sage-light object-cover" />
            <button type="button" onClick={() => setIds((prev) => prev.filter((x) => x !== id))}
              className="absolute -right-1.5 -top-1.5 rounded-full bg-rose-500 p-0.5 text-white"><Trash2 className="h-3 w-3" /></button>
          </div>
        ))}
        <label className="flex h-12 w-12 cursor-pointer items-center justify-center rounded border border-dashed border-sage-light text-forest-mid hover:bg-sage-surface">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
          <input type="file" accept="image/*" multiple className="hidden" onChange={pick} data-testid={testid} />
        </label>
      </div>
    </div>
  );
}

const WASTE_BLANK = { material: "", est_qty: 0, actual_qty: "", unit: "kg", pathway: "Recycle", destination: "", partner: "", notes: "", photo_ids: [] };
const LOT_BLANK = { location: "", area_sqm: 0, qty: "", unit: "m²", composition: "", condition: "", recommended_pathway: "Reuse", cost_sgd: "", notes: "", photo_ids: [] };

export default function CircularWastePanel({ packId, canManage, basePath }) {
  const base = basePath || `/tender-packs/${packId}/circular`;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drafting, setDrafting] = useState("");
  const [reports, setReports] = useState({ methodology: "", carpet: "" });

  // dialogs
  const [wasteDlg, setWasteDlg] = useState(null); // {mode, item}
  const [lotDlg, setLotDlg] = useState(null);
  const [targetsDlg, setTargetsDlg] = useState(null);
  const [partnerDlg, setPartnerDlg] = useState(null);
  const [splitDraft, setSplitDraft] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`${base}`);
      setData(r.data);
      setReports({ methodology: r.data.methodology_report || "", carpet: r.data.carpet_report || "" });
    } catch (e) { toast.error(apiErr(e, "Could not load the circular waste section")); }
    finally { setLoading(false); }
  }, [base]);

  useEffect(() => { load(); }, [load]);

  const enable = async () => {
    try { await api.post(`${base}/enable`); toast.success("Circular Waste section enabled"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const draft = async (key) => {
    setDrafting(key);
    try {
      const r = await api.post(`${base}/draft/${key}`);
      setData(r.data);
      setReports({ methodology: r.data.methodology_report || "", carpet: r.data.carpet_report || "" });
      toast.success("AI draft ready — review and edit");
    } catch (e) { toast.error(apiErr(e, "AI drafting failed")); }
    finally { setDrafting(""); }
  };

  const saveReport = async (key) => {
    try {
      const body = key === "methodology" ? { methodology_report: reports.methodology } : { carpet_report: reports.carpet };
      const r = await api.patch(`${base}`, body);
      setData(r.data); toast.success("Saved");
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (loading) return <Card className="border-sage-light p-6 text-sm text-muted-foreground">Loading circular waste…</Card>;

  if (!data?.enabled) {
    return (
      <Card className="border-dashed border-sage-light p-6" data-testid={CIRCULAR.root}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-sage-surface p-2.5"><Recycle className="h-5 w-5 text-forest-mid" /></div>
            <div>
              <h3 className="font-heading text-base font-semibold text-forest-dark">Circular Waste &amp; Disposal</h3>
              <p className="max-w-xl text-sm text-muted-foreground">Prove how project waste — and old carpet in particular — is avoided, reused, upcycled, recycled or responsibly disposed of. Captures an auditable register, computes live diversion numbers and drafts two tender-ready reports.</p>
            </div>
          </div>
          {canManage && <Button onClick={enable} className="bg-forest-mid hover:bg-forest-dark" data-testid={CIRCULAR.enableBtn}><Plus className="mr-1.5 h-4 w-4" /> Turn on this section</Button>}
        </div>
      </Card>
    );
  }

  const c = data.computed || {};
  const carpet = data.carpet || {};
  const pathways = data.pathways || [];
  const carpetPathways = data.carpet_pathways || [];

  // ---- waste CRUD ----
  const saveWaste = async () => {
    const it = wasteDlg.item;
    const body = { ...it, est_qty: Number(it.est_qty) || 0, actual_qty: it.actual_qty === "" ? null : Number(it.actual_qty) };
    try {
      if (wasteDlg.mode === "edit") await api.patch(`${base}/waste/${it.id}`, body);
      else await api.post(`${base}/waste`, body);
      setWasteDlg(null); load(); toast.success("Saved");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const delWaste = async (id) => { try { await api.delete(`${base}/waste/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };

  // ---- carpet lot CRUD ----
  const saveLot = async () => {
    const lot = lotDlg.item;
    const body = { ...lot, area_sqm: Number(lot.area_sqm) || 0, qty: lot.qty === "" ? null : Number(lot.qty), cost_sgd: lot.cost_sgd === "" ? null : Number(lot.cost_sgd) };
    try {
      if (lotDlg.mode === "edit") await api.patch(`${base}/carpet/${lot.id}`, body);
      else await api.post(`${base}/carpet`, body);
      setLotDlg(null); load(); toast.success("Saved");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const delLot = async (id) => { try { await api.delete(`${base}/carpet/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };

  const saveTargets = async () => {
    try { const r = await api.patch(`${base}`, { targets: targetsDlg }); setData(r.data); setTargetsDlg(null); toast.success("Targets saved"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const savePartner = async () => {
    try { const r = await api.patch(`${base}`, { carpet_partner: partnerDlg }); setData(r.data); setPartnerDlg(null); toast.success("Partner saved"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveSplit = async () => {
    const split = carpetPathways.map((p) => ({ pathway: p, qty: Number(splitDraft[p]) || 0 })).filter((x) => x.qty > 0);
    try { const r = await api.patch(`${base}`, { carpet_pathway_split: split }); setData(r.data); setSplitDraft(null); toast.success("Pathway split saved"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const STAT_TILES = [
    { k: "total_waste", label: `Waste handled (${c.unit || "kg"})`, icon: Boxes, val: c.total_waste },
    { k: "diversion_rate", label: "Diversion rate", icon: Leaf, val: `${c.diversion_rate ?? 0}%` },
    { k: "diverted", label: "Diverted", icon: Recycle, val: c.diverted },
    { k: "carpet_total", label: `Carpet (${carpet.unit || "m²"})`, icon: Package, val: c.carpet?.total_qty },
    { k: "carpet_diverted", label: "Carpet diverted", icon: CheckCircle2, val: `${c.carpet?.diversion_rate ?? 0}%` },
    { k: "evidence", label: "Evidence photos", icon: Camera, val: c.evidence_photos },
  ];

  return (
    <div className="space-y-4" data-testid={CIRCULAR.root}>
      {/* dashboard */}
      <Card className="border-sage-light p-5" data-testid={CIRCULAR.dashboard}>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-forest-dark"><Recycle className="h-4 w-4 text-forest-mid" /> Circular Waste &amp; Disposal</h3>
          <span className="rounded-full border border-forest-mid/30 bg-sage-surface px-2.5 py-1 text-xs font-semibold text-forest-dark">Live diversion {c.diversion_rate ?? 0}%</span>
        </div>
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
          {STAT_TILES.map((t) => (
            <div key={t.k} className="rounded-xl border border-sage-light bg-sage-surface p-3 text-center" data-testid={CIRCULAR.stat(t.k)}>
              <t.icon className="mx-auto h-4 w-4 text-forest-mid" />
              <p className="mt-1 font-heading text-xl font-bold text-forest-dark">{t.val ?? 0}</p>
              <p className="text-[10px] text-muted-foreground">{t.label}</p>
            </div>
          ))}
        </div>

        {/* pathway split bar */}
        {Object.keys(c.by_pathway || {}).length > 0 && (
          <div className="mt-4">
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Waste pathway split</p>
            <div className="flex h-3 w-full overflow-hidden rounded-full border border-sage-light">
              {pathways.filter((p) => c.by_pathway[p]).map((p) => (
                <div key={p} title={`${p}: ${c.pathway_pct[p]}%`} style={{ width: `${c.pathway_pct[p]}%` }}
                  className={PATHWAY_CLS[p]?.replace("text-", "").split(" ")[0] || "bg-sage-light"} />
              ))}
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {pathways.filter((p) => c.by_pathway[p]).map((p) => (
                <span key={p} className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${PATHWAY_CLS[p] || "bg-sage-light"}`}>{p} {c.pathway_pct[p]}%</span>
              ))}
            </div>
          </div>
        )}

        {/* targets vs actuals */}
        <div className="mt-4">
          <div className="mb-1.5 flex items-center justify-between">
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><Target className="h-3.5 w-3.5" /> Recovery &amp; diversion targets</p>
            {canManage && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setTargetsDlg({ ...(data.targets || {}) })} data-testid={CIRCULAR.targetsBtn}><Pencil className="mr-1 h-3 w-3" /> Edit</Button>}
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {(c.target_status || []).map((t) => (
              <div key={t.key} className="rounded-lg border border-sage-light bg-white p-3" data-testid={CIRCULAR.targetRow(t.key)}>
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-forest-dark">{t.label}</span>
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${t.on_track ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                    {t.on_track ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />} {t.on_track ? "On track" : "Short"}
                  </span>
                </div>
                <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-sage-surface">
                  <div className={`h-full rounded-full ${t.on_track ? "bg-forest-mid" : "bg-amber-400"}`} style={{ width: `${Math.min(100, t.actual)}%` }} />
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">Actual {t.actual}% · Target {t.target}%</p>
              </div>
            ))}
            {(c.target_status || []).length === 0 && <p className="text-xs text-muted-foreground">No targets set yet.</p>}
          </div>
        </div>
      </Card>

      {/* waste register */}
      <Card className="border-sage-light p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-forest-dark"><Boxes className="h-4 w-4 text-forest-mid" /> Waste &amp; material register</h3>
          {canManage && <Button size="sm" onClick={() => setWasteDlg({ mode: "add", item: { ...WASTE_BLANK } })} className="bg-forest-mid hover:bg-forest-dark" data-testid={CIRCULAR.wasteAddBtn}><Plus className="mr-1 h-4 w-4" /> Add line</Button>}
        </div>
        {(data.waste_items || []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No register lines yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="py-2 pr-2">Material</th><th className="px-2">Qty</th><th className="px-2">Pathway</th><th className="px-2">Destination</th><th className="px-2">Evidence</th>{canManage && <th />}</tr></thead>
              <tbody>
                {data.waste_items.map((it) => (
                  <tr key={it.id} className="border-b last:border-0" data-testid={CIRCULAR.wasteRow(it.id)}>
                    <td className="py-2 pr-2 font-medium text-forest-dark">{it.material}</td>
                    <td className="px-2 whitespace-nowrap">{it.actual_qty ?? it.est_qty} {it.unit}</td>
                    <td className="px-2"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${PATHWAY_CLS[it.pathway] || "bg-sage-light"}`}>{it.pathway}</span></td>
                    <td className="px-2 text-xs text-muted-foreground">{it.destination || "—"}{it.partner ? ` · ${it.partner}` : ""}</td>
                    <td className="px-2">
                      <div className="flex gap-1">{(it.photo_ids || []).slice(0, 3).map((id) => <img key={id} src={photoUrl({ id })} alt="" className="h-7 w-7 rounded border border-sage-light object-cover" />)}</div>
                    </td>
                    {canManage && <td className="whitespace-nowrap text-right">
                      <button onClick={() => setWasteDlg({ mode: "edit", item: { ...WASTE_BLANK, ...it, actual_qty: it.actual_qty ?? "" } })} className="p-1 text-forest-mid" data-testid={CIRCULAR.wasteEditBtn(it.id)}><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => delWaste(it.id)} className="p-1 text-rose-500" data-testid={CIRCULAR.wasteDeleteBtn(it.id)}><Trash2 className="h-4 w-4" /></button>
                    </td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* carpet module */}
      <Card className="border-sage-light p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-forest-dark"><Package className="h-4 w-4 text-forest-mid" /> Old carpet — inventory &amp; circularity</h3>
          {canManage && <Button size="sm" onClick={() => setLotDlg({ mode: "add", item: { ...LOT_BLANK } })} className="bg-forest-mid hover:bg-forest-dark" data-testid={CIRCULAR.carpetAddBtn}><Plus className="mr-1 h-4 w-4" /> Add lot</Button>}
        </div>
        {(carpet.lots || []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No carpet lots recorded yet.</p>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {carpet.lots.map((lot) => (
              <div key={lot.id} className="rounded-xl border border-sage-light bg-white p-4" data-testid={CIRCULAR.carpetRow(lot.id)}>
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold text-forest-dark">{lot.location || "Carpet lot"}</p>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${PATHWAY_CLS[lot.recommended_pathway] || "bg-sage-light"}`}>{lot.recommended_pathway}</span>
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">{lot.qty ?? lot.area_sqm} {lot.unit} · {lot.composition || "composition n/a"}</p>
                {lot.condition && <p className="mt-0.5 text-xs text-muted-foreground">Condition: {lot.condition}</p>}
                {lot.notes && <p className="mt-1 text-xs text-muted-foreground">{lot.notes}</p>}
                {(lot.photo_ids || []).length > 0 && <div className="mt-2 flex gap-1.5">{lot.photo_ids.slice(0, 4).map((id) => <img key={id} src={photoUrl({ id })} alt="" className="h-12 w-12 rounded border border-sage-light object-cover" />)}</div>}
                {canManage && <div className="mt-2 flex gap-2">
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setLotDlg({ mode: "edit", item: { ...LOT_BLANK, ...lot, qty: lot.qty ?? "", cost_sgd: lot.cost_sgd ?? "" } })} data-testid={CIRCULAR.carpetEditBtn(lot.id)}><Pencil className="mr-1 h-3 w-3" /> Edit</Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-rose-500" onClick={() => delLot(lot.id)} data-testid={CIRCULAR.carpetDeleteBtn(lot.id)}><Trash2 className="mr-1 h-3 w-3" /> Delete</Button>
                </div>}
              </div>
            ))}
          </div>
        )}

        {/* selected pathway split */}
        <div className="mt-4 rounded-xl border border-sage-light bg-sage-surface/50 p-4" data-testid={CIRCULAR.pathwaySplit}>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Selected circularity pathway ({carpet.unit || "m²"})</p>
            {canManage && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setSplitDraft(Object.fromEntries((carpet.pathway_split || []).map((s) => [s.pathway, s.qty])))} data-testid={CIRCULAR.splitEditBtn}><Pencil className="mr-1 h-3 w-3" /> Edit split</Button>}
          </div>
          {Object.keys(c.carpet?.split || {}).length === 0 ? (
            <p className="text-xs text-muted-foreground">No pathway split set.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {Object.entries(c.carpet.split).map(([p, q]) => (
                <span key={p} className={`rounded-full px-2.5 py-1 text-xs font-semibold ${PATHWAY_CLS[p] || "bg-sage-light"}`}>{p}: {q} {carpet.unit || "m²"} ({c.carpet.split_pct[p]}%)</span>
              ))}
            </div>
          )}
          <p className="mt-2 text-xs text-forest-dark"><b>{c.carpet?.landfill_avoided ?? 0} {carpet.unit || "m²"}</b> diverted from landfill ({c.carpet?.diversion_rate ?? 0}%).</p>
        </div>

        {/* upcycling partner */}
        <div className="mt-3 flex items-start justify-between gap-3 rounded-xl border border-sage-light bg-white p-4">
          <div className="flex items-start gap-3">
            <Handshake className="mt-0.5 h-5 w-5 text-forest-mid" />
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Upcycling partner</p>
              <p className="font-medium text-forest-dark">{carpet.partner?.name || "Not set"}</p>
              {carpet.partner?.contact && <p className="text-xs text-muted-foreground">{carpet.partner.contact}</p>}
              {carpet.partner?.cost_sgd != null && carpet.partner?.cost_sgd !== "" && <p className="text-xs text-muted-foreground">Programme cost: {fmtSGD(carpet.partner.cost_sgd)}</p>}
              {carpet.partner?.notes && <p className="mt-1 max-w-md text-xs text-muted-foreground">{carpet.partner.notes}</p>}
            </div>
          </div>
          {canManage && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setPartnerDlg({ ...(carpet.partner || {}) })} data-testid={CIRCULAR.partnerBtn}><Pencil className="mr-1 h-3 w-3" /> Edit</Button>}
        </div>
      </Card>

      {/* reports */}
      {[{ key: "methodology", title: "Sustainable Disposal Methodology Report", ai: data.methodology_ai },
        { key: "carpet", title: "Old Carpet Upcycling & Circularity Report", ai: data.carpet_ai }].map((rep) => (
        <Card key={rep.key} className="border-sage-light p-5">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-heading text-base font-semibold text-forest-dark">{rep.title}</h3>
            {canManage && <Button size="sm" variant="outline" onClick={() => draft(rep.key)} disabled={drafting === rep.key} data-testid={CIRCULAR.reportDraftBtn(rep.key)}>
              {drafting === rep.key ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4 text-forest-mid" />} Draft with AI
            </Button>}
          </div>
          {rep.ai && (
            <div className="mb-3 flex items-start gap-2 rounded-r-lg border-l-4 border-amber-500 bg-amber-50/90 p-3">
              <Bot className="mt-0.5 h-4 w-4 text-amber-600" />
              <p className="text-xs text-amber-900"><b>AI-generated draft.</b> Review and edit before issuing.</p>
            </div>
          )}
          {canManage ? (
            <>
              <Textarea rows={10} value={reports[rep.key]} onChange={(e) => setReports((p) => ({ ...p, [rep.key]: e.target.value }))}
                placeholder="Write this report, or draft it from your captured register with AI…" data-testid={CIRCULAR.reportInput(rep.key)} />
              <Button size="sm" className="mt-2 bg-forest-mid hover:bg-forest-dark" onClick={() => saveReport(rep.key)} data-testid={CIRCULAR.reportSaveBtn(rep.key)}>Save report</Button>
            </>
          ) : (
            <div className="whitespace-pre-wrap rounded-lg bg-sage-surface p-4 text-sm text-forest-dark">{reports[rep.key] || "Not drafted yet."}</div>
          )}
        </Card>
      ))}

      {/* ===== dialogs ===== */}
      {/* waste */}
      <Dialog open={!!wasteDlg} onOpenChange={(o) => !o && setWasteDlg(null)}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-lg" data-testid={CIRCULAR.wasteDialog}>
          <DialogHeader><DialogTitle className="font-heading">{wasteDlg?.mode === "edit" ? "Edit" : "Add"} register line</DialogTitle></DialogHeader>
          {wasteDlg && (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Material / stream</Label><Input value={wasteDlg.item.material} onChange={(e) => setWasteDlg((d) => ({ ...d, item: { ...d.item, material: e.target.value } }))} data-testid={CIRCULAR.wasteField("material")} /></div>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1.5"><Label>Est. qty</Label><Input type="number" value={wasteDlg.item.est_qty} onChange={(e) => setWasteDlg((d) => ({ ...d, item: { ...d.item, est_qty: e.target.value } }))} data-testid={CIRCULAR.wasteField("est_qty")} /></div>
                <div className="space-y-1.5"><Label>Actual qty</Label><Input type="number" value={wasteDlg.item.actual_qty} onChange={(e) => setWasteDlg((d) => ({ ...d, item: { ...d.item, actual_qty: e.target.value } }))} data-testid={CIRCULAR.wasteField("actual_qty")} /></div>
                <div className="space-y-1.5"><Label>Unit</Label><Input value={wasteDlg.item.unit} onChange={(e) => setWasteDlg((d) => ({ ...d, item: { ...d.item, unit: e.target.value } }))} data-testid={CIRCULAR.wasteField("unit")} /></div>
              </div>
              <div className="space-y-1.5"><Label>Pathway</Label>
                <Select value={wasteDlg.item.pathway} onValueChange={(v) => setWasteDlg((d) => ({ ...d, item: { ...d.item, pathway: v } }))}>
                  <SelectTrigger data-testid={CIRCULAR.wasteField("pathway")}><SelectValue /></SelectTrigger>
                  <SelectContent>{pathways.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5"><Label>Destination</Label><Input value={wasteDlg.item.destination} onChange={(e) => setWasteDlg((d) => ({ ...d, item: { ...d.item, destination: e.target.value } }))} data-testid={CIRCULAR.wasteField("destination")} /></div>
                <div className="space-y-1.5"><Label>Partner / carrier</Label><Input value={wasteDlg.item.partner} onChange={(e) => setWasteDlg((d) => ({ ...d, item: { ...d.item, partner: e.target.value } }))} data-testid={CIRCULAR.wasteField("partner")} /></div>
              </div>
              <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={wasteDlg.item.notes} onChange={(e) => setWasteDlg((d) => ({ ...d, item: { ...d.item, notes: e.target.value } }))} data-testid={CIRCULAR.wasteField("notes")} /></div>
              <div className="space-y-1.5"><Label>Photo evidence</Label><PhotoPicker ids={wasteDlg.item.photo_ids} setIds={(fn) => setWasteDlg((d) => ({ ...d, item: { ...d.item, photo_ids: typeof fn === "function" ? fn(d.item.photo_ids) : fn } }))} testid={CIRCULAR.wastePhoto} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setWasteDlg(null)}>Cancel</Button><Button onClick={saveWaste} disabled={!wasteDlg?.item.material} className="bg-forest-mid hover:bg-forest-dark" data-testid={CIRCULAR.wasteSaveBtn}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* carpet lot */}
      <Dialog open={!!lotDlg} onOpenChange={(o) => !o && setLotDlg(null)}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-lg" data-testid={CIRCULAR.carpetDialog}>
          <DialogHeader><DialogTitle className="font-heading">{lotDlg?.mode === "edit" ? "Edit" : "Add"} carpet lot</DialogTitle></DialogHeader>
          {lotDlg && (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Location</Label><Input value={lotDlg.item.location} onChange={(e) => setLotDlg((d) => ({ ...d, item: { ...d.item, location: e.target.value } }))} data-testid={CIRCULAR.carpetField("location")} /></div>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1.5"><Label>Area (m²)</Label><Input type="number" value={lotDlg.item.area_sqm} onChange={(e) => setLotDlg((d) => ({ ...d, item: { ...d.item, area_sqm: e.target.value } }))} data-testid={CIRCULAR.carpetField("area_sqm")} /></div>
                <div className="space-y-1.5"><Label>Qty</Label><Input type="number" value={lotDlg.item.qty} onChange={(e) => setLotDlg((d) => ({ ...d, item: { ...d.item, qty: e.target.value } }))} data-testid={CIRCULAR.carpetField("qty")} /></div>
                <div className="space-y-1.5"><Label>Unit</Label><Input value={lotDlg.item.unit} onChange={(e) => setLotDlg((d) => ({ ...d, item: { ...d.item, unit: e.target.value } }))} data-testid={CIRCULAR.carpetField("unit")} /></div>
              </div>
              <div className="space-y-1.5"><Label>Material composition</Label><Input value={lotDlg.item.composition} onChange={(e) => setLotDlg((d) => ({ ...d, item: { ...d.item, composition: e.target.value } }))} data-testid={CIRCULAR.carpetField("composition")} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5"><Label>Condition</Label><Input value={lotDlg.item.condition} onChange={(e) => setLotDlg((d) => ({ ...d, item: { ...d.item, condition: e.target.value } }))} data-testid={CIRCULAR.carpetField("condition")} /></div>
                <div className="space-y-1.5"><Label>Recommended pathway</Label>
                  <Select value={lotDlg.item.recommended_pathway} onValueChange={(v) => setLotDlg((d) => ({ ...d, item: { ...d.item, recommended_pathway: v } }))}>
                    <SelectTrigger data-testid={CIRCULAR.carpetField("recommended_pathway")}><SelectValue /></SelectTrigger>
                    <SelectContent>{carpetPathways.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5"><Label>Cost (SGD, optional)</Label><Input type="number" value={lotDlg.item.cost_sgd} onChange={(e) => setLotDlg((d) => ({ ...d, item: { ...d.item, cost_sgd: e.target.value } }))} data-testid={CIRCULAR.carpetField("cost_sgd")} /></div>
              <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={lotDlg.item.notes} onChange={(e) => setLotDlg((d) => ({ ...d, item: { ...d.item, notes: e.target.value } }))} data-testid={CIRCULAR.carpetField("notes")} /></div>
              <div className="space-y-1.5"><Label>Photo evidence</Label><PhotoPicker ids={lotDlg.item.photo_ids} setIds={(fn) => setLotDlg((d) => ({ ...d, item: { ...d.item, photo_ids: typeof fn === "function" ? fn(d.item.photo_ids) : fn } }))} testid={CIRCULAR.carpetPhoto} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setLotDlg(null)}>Cancel</Button><Button onClick={saveLot} className="bg-forest-mid hover:bg-forest-dark" data-testid={CIRCULAR.carpetSaveBtn}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* targets */}
      <Dialog open={!!targetsDlg} onOpenChange={(o) => !o && setTargetsDlg(null)}>
        <DialogContent className="sm:max-w-md" data-testid={CIRCULAR.targetsDialog}>
          <DialogHeader><DialogTitle className="font-heading">Recovery &amp; diversion targets (%)</DialogTitle></DialogHeader>
          {targetsDlg && (
            <div className="space-y-3">
              {[["diversion_pct", "Overall diversion"], ["reuse_pct", "Reuse / upcycle"], ["recycle_pct", "Recycle / recover"], ["carpet_diversion_pct", "Carpet diverted from landfill"]].map(([k, label]) => (
                <div key={k} className="space-y-1.5"><Label>{label}</Label><Input type="number" value={targetsDlg[k] ?? ""} onChange={(e) => setTargetsDlg((d) => ({ ...d, [k]: e.target.value === "" ? null : Number(e.target.value) }))} data-testid={CIRCULAR.targetField(k)} /></div>
              ))}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setTargetsDlg(null)}>Cancel</Button><Button onClick={saveTargets} className="bg-forest-mid hover:bg-forest-dark" data-testid={CIRCULAR.targetsSaveBtn}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* partner */}
      <Dialog open={!!partnerDlg} onOpenChange={(o) => !o && setPartnerDlg(null)}>
        <DialogContent className="sm:max-w-md" data-testid={CIRCULAR.partnerDialog}>
          <DialogHeader><DialogTitle className="font-heading">Upcycling partner</DialogTitle></DialogHeader>
          {partnerDlg && (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Partner name</Label><Input value={partnerDlg.name || ""} onChange={(e) => setPartnerDlg((d) => ({ ...d, name: e.target.value }))} data-testid={CIRCULAR.partnerField("name")} /></div>
              <div className="space-y-1.5"><Label>Contact</Label><Input value={partnerDlg.contact || ""} onChange={(e) => setPartnerDlg((d) => ({ ...d, contact: e.target.value }))} data-testid={CIRCULAR.partnerField("contact")} /></div>
              <div className="space-y-1.5"><Label>Programme cost (SGD, optional)</Label><Input type="number" value={partnerDlg.cost_sgd ?? ""} onChange={(e) => setPartnerDlg((d) => ({ ...d, cost_sgd: e.target.value === "" ? null : Number(e.target.value) }))} data-testid={CIRCULAR.partnerField("cost_sgd")} /></div>
              <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={3} value={partnerDlg.notes || ""} onChange={(e) => setPartnerDlg((d) => ({ ...d, notes: e.target.value }))} data-testid={CIRCULAR.partnerField("notes")} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setPartnerDlg(null)}>Cancel</Button><Button onClick={savePartner} className="bg-forest-mid hover:bg-forest-dark" data-testid={CIRCULAR.partnerSaveBtn}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* pathway split */}
      <Dialog open={!!splitDraft} onOpenChange={(o) => !o && setSplitDraft(null)}>
        <DialogContent className="sm:max-w-md" data-testid={CIRCULAR.splitDialog}>
          <DialogHeader><DialogTitle className="font-heading">Carpet pathway split ({carpet.unit || "m²"})</DialogTitle></DialogHeader>
          {splitDraft && (
            <div className="space-y-3">
              {carpetPathways.map((p) => (
                <div key={p} className="space-y-1.5"><Label>{p}</Label><Input type="number" value={splitDraft[p] ?? ""} onChange={(e) => setSplitDraft((d) => ({ ...d, [p]: e.target.value }))} data-testid={CIRCULAR.splitField(p)} /></div>
              ))}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setSplitDraft(null)}>Cancel</Button><Button onClick={saveSplit} className="bg-forest-mid hover:bg-forest-dark" data-testid={CIRCULAR.splitSaveBtn}>Save split</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
